import React, { useEffect, useState, useCallback } from 'react';
import {
  scoutApi, ScoutOpenTrade, ScoutInstrument, INSTRUMENT_DISPLAY,
} from '../../services/api';

const fmtPx = (n: number | null | undefined, digits = 4): string =>
  n == null ? '—' : n.toFixed(digits);

const fmtPL = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;
};

const heldLabel = (h: number | null): string => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(1)}h`;
};

const directionClass = (d: string): string =>
  d === 'BUY' ? 'pl-positive' : 'pl-negative';

const OpenPositions: React.FC = () => {
  const [trades, setTrades] = useState<ScoutOpenTrade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [closeNotes, setCloseNotes] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTrades(await scoutApi.getOpenTrades());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
    // Poll every 20s so unrealized P/L stays roughly current
    const t = setInterval(fetch, 20_000);
    return () => clearInterval(t);
  }, [fetch]);

  const doClose = async (trade_id: string) => {
    setClosingId(trade_id);
    setError(null);
    try {
      const result = await scoutApi.closeTrade(trade_id, closeNotes || undefined);
      console.info(`Closed ${trade_id}:`, result);
      setConfirmId(null);
      setCloseNotes('');
      await fetch();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'close failed');
    } finally {
      setClosingId(null);
    }
  };

  // Empty state — don't render the section at all if no opens.
  // Caller (LLM.tsx) can choose to hide the section title too if desired.
  if (!loading && trades.length === 0 && !error) {
    return null;
  }

  return (
    <div className="open-positions-section">
      <div className="open-positions-header">
        <h2 className="open-positions-title">
          Open Positions
          {trades.length > 0 && <span className="open-positions-count"> ({trades.length})</span>}
        </h2>
        <button className="reviews-refresh-btn" onClick={fetch} disabled={loading}>
          {loading ? '...' : '↻ Refresh'}
        </button>
      </div>

      {error && <div className="advisor-error"><strong>Error:</strong> {error}</div>}

      <div className="open-positions-list">
        {trades.map(t => {
          const isPending = closingId === t.trade_id;
          const isConfirming = confirmId === t.trade_id;
          const plPositive = (t.unrealized_pl ?? 0) >= 0;
          return (
            <div key={t.trade_id} className={`open-position-card ${plPositive ? 'op-positive' : 'op-negative'}`}>
              <div className="op-row-top">
                <span className="instrument-badge">
                  {INSTRUMENT_DISPLAY[t.instrument as ScoutInstrument] || t.instrument}
                </span>
                <span className={`op-direction ${directionClass(t.direction)}`}>{t.direction}</span>
                <strong className="op-entry">@ ${fmtPx(t.entry_price)}</strong>
                <span className="trigger-meta">size {t.size}</span>
                <span className="trigger-meta">held {heldLabel(t.hours_held)}</span>
                {t.entry_setup_type && (
                  <span className="trigger-meta trigger-meta-dim">{t.entry_setup_type}</span>
                )}
                {t.broker_status !== 'found' && t.broker_status !== 'found_via_fallback' && (
                  <span className="trigger-meta" style={{ color: '#f1c40f' }}>
                    broker: {t.broker_status}
                  </span>
                )}
              </div>

              <div className="op-row-middle">
                <div className="op-stat">
                  <div className="op-stat-label">Current</div>
                  <div className="op-stat-value">${fmtPx(t.current_price)}</div>
                </div>
                <div className="op-stat">
                  <div className="op-stat-label">Stop</div>
                  <div className="op-stat-value">${fmtPx(t.stop_price)}</div>
                </div>
                <div className="op-stat">
                  <div className="op-stat-label">TP1</div>
                  <div className="op-stat-value">${fmtPx(t.take_profit)}</div>
                </div>
                <div className="op-stat">
                  <div className="op-stat-label">TP2</div>
                  <div className="op-stat-value">{t.take_profit_2 != null ? `$${fmtPx(t.take_profit_2)}` : '—'}</div>
                </div>
                <div className="op-stat op-stat-pl">
                  <div className="op-stat-label">Unrealized P/L</div>
                  <div className={`op-stat-value ${plPositive ? 'pl-positive' : 'pl-negative'}`}>
                    {fmtPL(t.unrealized_pl)} MXN
                  </div>
                </div>
              </div>

              <div className="op-row-bottom">
                {!isConfirming ? (
                  <button
                    className="op-close-btn"
                    onClick={() => setConfirmId(t.trade_id)}
                    disabled={isPending}
                  >
                    Close at market
                  </button>
                ) : (
                  <div className="op-confirm">
                    <input
                      type="text"
                      placeholder="Reason (optional, for the audit log)"
                      value={closeNotes}
                      onChange={e => setCloseNotes(e.target.value)}
                      maxLength={200}
                      autoFocus
                    />
                    <button
                      className="op-close-btn op-close-btn-confirm"
                      onClick={() => doClose(t.trade_id)}
                      disabled={isPending}
                    >
                      {isPending ? 'Closing...' : 'Confirm close'}
                    </button>
                    <button
                      className="op-close-btn op-close-btn-cancel"
                      onClick={() => { setConfirmId(null); setCloseNotes(''); }}
                      disabled={isPending}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OpenPositions;
